import React, { useEffect, useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { fetchFuelWithCache } from '../services/fuelClientCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * FuelPriceMap — Phase 5+ Price Map heatmap (audit 2026-02-12)
 *
 * Renders a leaflet map centred on the user's location (or UK centre) with
 * a heatmap layer where:
 *   • Red hotspots  = MOST EXPENSIVE prices (avoid).
 *   • Green/cool    = CHEAPEST prices (head here).
 *
 * Data comes from /api/fuel/heatmap, which returns [lat, lng, intensity]
 * tuples already normalised in [0, 1]. The cache module hands us SWR + ETag
 * + offline fallback for free.
 *
 * Why leaflet over Google Maps / Mapbox? No API key needed, free, ~40KB,
 * works offline (cached tiles), perfect for PWAs.
 */
const FUEL_TYPES = [
  { key: 'E10', label: 'Unleaded' },
  { key: 'B7', label: 'Diesel' },
  { key: 'E5', label: 'Super Unleaded' },
  { key: 'SDV', label: 'Super Diesel' },
];

// UK centre (a bit south of Birmingham for nice framing).
const UK_DEFAULT = { lat: 52.4862, lng: -1.8904, zoom: 6 };

const FuelPriceMap = ({ userLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const heatLayerRef = useRef(null);
  const [fuelType, setFuelType] = useState('E10');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ count: 0, minPrice: null, maxPrice: null, medianPrice: null });

  // Initialise the leaflet map once.
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    const start = userLocation
      ? { lat: userLocation.latitude, lng: userLocation.longitude, zoom: 11 }
      : UK_DEFAULT;
    const map = L.map(mapRef.current, {
      center: [start.lat, start.lng],
      zoom: start.zoom,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '© OpenStreetMap',
    }).addTo(map);
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      heatLayerRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Re-fetch heatmap data whenever fuelType (or user location) changes.
  useEffect(() => {
    let cancelled = false;
    const fetchHeat = async () => {
      setLoading(true);
      setError(null);
      // Use a bbox around the user's location for relevance + faster loads.
      // Fall back to UK-wide if no location.
      const bbox = userLocation
        ? `${(userLocation.latitude - 0.5).toFixed(4)},${(userLocation.longitude - 0.5).toFixed(4)},${(userLocation.latitude + 0.5).toFixed(4)},${(userLocation.longitude + 0.5).toFixed(4)}`
        : null;
      const params = new URLSearchParams({ fuel_type: fuelType, max_points: '1500' });
      if (bbox) params.append('bbox', bbox);
      const url = `${BACKEND_URL}/api/fuel/heatmap?${params}`;
      const cacheKey = `heatmap:${params.toString()}`;
      let result = await fetchFuelWithCache(cacheKey, url, {});

      // iter174: If premium endpoint is auth-gated (401) fall back to the
      // public marketing endpoint so anonymous visitors on /price-map still
      // see the heatmap. Public endpoint caps at 800 points.
      const is401 = !result?.data && (
        result?.error?.message?.includes('401') ||
        result?.errorKind === 'client'
      );
      if (is401) {
        const pubParams = new URLSearchParams({ fuel_type: fuelType, max_points: '500' });
        const pubUrl = `${BACKEND_URL}/api/fuel/heatmap/public?${pubParams}`;
        const pubKey = `heatmap-public:${pubParams.toString()}`;
        result = await fetchFuelWithCache(pubKey, pubUrl, {});
      }

      if (cancelled || !result.data) {
        if (cancelled) return;
        setError('Could not load the fuel price map.');
        setLoading(false);
        return;
      }
      const { points, count, min_price, max_price, median_price } = result.data;
      setMeta({ count: count || 0, minPrice: min_price, maxPrice: max_price, medianPrice: median_price });

      // Render the heat layer.
      const map = mapInstanceRef.current;
      if (map) {
        if (heatLayerRef.current) {
          map.removeLayer(heatLayerRef.current);
          heatLayerRef.current = null;
        }
        if (points && points.length) {
          heatLayerRef.current = L.heatLayer(points, {
            radius: 22,
            blur: 18,
            maxZoom: 12,
            minOpacity: 0.4,
            // Cheaper = green, mid = yellow, expensive = red.
            gradient: { 0.0: '#10b981', 0.45: '#fbbf24', 0.7: '#f97316', 1.0: '#ef4444' },
          }).addTo(map);
        }
      }
      setLoading(false);
    };
    fetchHeat();
    return () => { cancelled = true; };
  }, [fuelType, userLocation]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="fuel-price-heatmap">
      {/* Fuel-type chooser + legend */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Heatmap</span>
          <select
            value={fuelType}
            onChange={(e) => setFuelType(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white"
            data-testid="heatmap-fuel-type-select"
          >
            {FUEL_TYPES.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-500" data-testid="heatmap-legend">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Cheap</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />Mid</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Expensive</span>
        </div>
      </div>

      {/* Map container */}
      <div ref={mapRef} style={{ height: 420, width: '100%' }} data-testid="fuel-price-heatmap-canvas" />

      {/* Meta bar */}
      <div className="flex items-center justify-between px-4 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-100">
        {loading ? (
          <span data-testid="heatmap-loading">Loading heatmap…</span>
        ) : error ? (
          <span className="text-red-500" data-testid="heatmap-error">{error}</span>
        ) : (
          <>
            <span data-testid="heatmap-stats">
              {meta.count} stations · range £{((meta.minPrice ?? 0) / 100).toFixed(2)}–£{((meta.maxPrice ?? 0) / 100).toFixed(2)}/L
            </span>
            <span data-testid="heatmap-median">median £{((meta.medianPrice ?? 0) / 100).toFixed(2)}/L</span>
          </>
        )}
      </div>
    </div>
  );
};

export default FuelPriceMap;
